import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCollaboratorByIdQuery } from '@/features/Collaborators/Data';
import { useToast } from '@/hooks/useToast';
import { uploadFile } from '@/utils/storage';
import { BaseModal } from '@/components/shared';
import type { Movimentacao, ConfigRegrasCliente, MovimentacaoEquipamentoView } from '../../../models';
import {
  mesclarEquipamentosMovimentacao,
  calcularEtapaFotos,
  fotosMinimasDoItem,
  ehItemSimples,
  EMPRESA_RARO,
} from '../../../models';
import { termoPdfService, type ItemEquipamentoTermo, type TermoPdfDados } from '../../../services';
import { useMovimentacaoMutations } from '../../../hooks/data';
import {
  TIPO_EQUIPAMENTO_LABELS,
  CONDICAO_EQUIPAMENTO_LABELS,
  ACESSORIO_LABELS,
} from '../solicitacaoDetailsPage.definitions';

interface GerarTermoButtonProps {
  solicitacaoId: string;
  movimentacao: Movimentacao;
  colaboradorId: string | null;
  clienteNome: string | null;
  regraCliente?: ConfigRegrasCliente;
}

/** Sede da Raro — usado na linha de data do topo do Termo (diferente da cidade do
 * colaborador, usada na declaração "declaro ter recebido/DEVOLVIDO, em..."). */
const CIDADE_SEDE_RARO = 'Belo Horizonte';

/** Firestore Timestamp ou já Date/string (dados legados) — mesmo padrão já usado em
 * MovimentacaoLogisticaSection pra ler dataEnvio/dataRecebimento. */
function paraDate(timestamp: unknown): Date | null {
  if (!timestamp) return null;
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  return new Date(timestamp as string);
}

const TITULO_ARQUIVO_POR_TIPO_FLUXO: Record<Movimentacao['tipoFluxo'], string> = {
  envio: 'Termo de Responsabilidade',
  devolucao: 'Termo de Devolução',
};

/** Remove caracteres inválidos em nome de arquivo (Windows/macOS) — mantém acentos. */
function sanitizarNomeArquivo(nome: string): string {
  return nome.replace(/[\\/:*?"<>|]/g, '').trim();
}

/** Identifica o equipamento nas páginas de fotos (ex. "Notebook — Patrimônio RLMP071"). */
function legendaEquipamento(item: MovimentacaoEquipamentoView): string {
  return [
    item.tipoEquipamento ? TIPO_EQUIPAMENTO_LABELS[item.tipoEquipamento] : 'Equipamento',
    item.patrimonio ? `Patrimônio ${item.patrimonio}` : item.modelo,
  ]
    .filter(Boolean)
    .join(' — ');
}

/**
 * Botões "Visualizar"/"Gerar Termo" — montam o PDF (termoPdfService) com os dados já
 * existentes da Movimentação/Solicitação/Colaborador. "Visualizar" só abre o PDF numa
 * aba nova, sem gravar nada. "Gerar Termo" baixa, salva no Storage e marca
 * `etapas.termo` — na 1ª vez, gera direto; se já existe um termo gerado antes, pergunta
 * primeiro se é pra baixar o que já existe ou gerar de novo com os dados atuais (evita
 * regerar à toa quando nada mudou, mas sem esconder a opção). Habilitados só quando todo
 * item de equipamento já tem patrimônio/modelo/condição preenchidos.
 */
export default function GerarTermoButton({
  solicitacaoId,
  movimentacao,
  colaboradorId,
  clienteNome,
  regraCliente,
}: GerarTermoButtonProps) {
  const toast = useToast();
  const { user } = useAuth();
  const { atualizarMovimentacao } = useMovimentacaoMutations(solicitacaoId);
  const { data: colaborador } = useCollaboratorByIdQuery(colaboradorId ?? undefined);
  const { data: assinanteRaro } = useCollaboratorByIdQuery(user?.collaboratorId ?? undefined);
  const [gerando, setGerando] = useState(false);
  const [visualizando, setVisualizando] = useState(false);
  const [mostrarEscolha, setMostrarEscolha] = useState(false);

  const itens = mesclarEquipamentosMovimentacao(movimentacao.equipamentosIntake, movimentacao.equipamentosExecucao);
  const podeGerar =
    itens.length > 0 &&
    itens.every(
      (item) =>
        item.patrimonio &&
        // Cartão de acesso e chip não têm modelo (no termo sai "—").
        (item.modelo || ehItemSimples(item.tipoEquipamento)) &&
        item.tipoEquipamento &&
        item.condicao &&
        ((item.acessorios && item.acessorios.length > 0) || Boolean(item.acessoriosOutros))
    );

  // A declaração do termo leva a data do fato (envio: quando recebeu; devolução: quando
  // postou/entregou) — gerar sem ela saía com a data de hoje. "Visualizar" segue livre.
  const dataDaDeclaracao = movimentacao.tipoFluxo === 'envio' ? movimentacao.dataRecebimento : movimentacao.dataEnvio;
  const faltaDataDeclaracao = !dataDaDeclaracao;
  const avisoFaltaData =
    movimentacao.tipoFluxo === 'envio'
      ? 'Informe a Data de Recebimento na logística — ela entra no termo.'
      : 'Informe a Data de Envio (quando a pessoa postou/entregou) na logística — ela entra no termo.';

  /** Monta os dados do termo a partir do que já está carregado — reaproveitado por
   * "Visualizar" e "Gerar Termo", que só diferem no que fazem com o PDF resultante. */
  const montarDadosTermo = (): TermoPdfDados | null => {
    if (!colaborador) return null;

    const algumDoCliente = itens.some((item) => item.propriedadeEquipamento === 'cliente');
    const empresa =
      algumDoCliente && regraCliente?.nomeEmpresaProprietaria && regraCliente?.cnpjEmpresaProprietaria
        ? { nome: regraCliente.nomeEmpresaProprietaria, cnpj: regraCliente.cnpjEmpresaProprietaria }
        : EMPRESA_RARO;

    if (algumDoCliente && empresa === EMPRESA_RARO) {
      toast.info(
        `Cliente "${clienteNome ?? ''}" não tem empresa proprietária configurada — usando dados da Raro no termo.`
      );
    }

    // Envio: data que o colaborador confirmou ter recebido. Devolução: data que ele
    // postou/entregou de volta (ver comentário em MovimentacaoLogisticaSection sobre
    // qual campo cada fluxo usa). Sem a data ainda registrada, cai pra hoje.
    const dataDeclaracao = paraDate(dataDaDeclaracao) ?? new Date();
    if (!dataDaDeclaracao) {
      // Só acontece no "Visualizar" (o Gerar fica bloqueado sem a data).
      toast.info('Prévia com a data de hoje — a data da logística ainda não foi informada.');
    }

    const equipamentos: ItemEquipamentoTermo[] = itens.map((item) => ({
      equipamento: item.tipoEquipamento ? TIPO_EQUIPAMENTO_LABELS[item.tipoEquipamento] : '—',
      modeloMarca: item.modelo ?? '—',
      condicao: item.condicao ? (CONDICAO_EQUIPAMENTO_LABELS[item.condicao] ?? item.condicao) : '—',
      patrimonio: item.patrimonio ?? '—',
      nSerie: item.nSerie ?? '—',
      acessorios:
        [
          ...(item.acessorios ?? []).map((valor) => ACESSORIO_LABELS[valor as keyof typeof ACESSORIO_LABELS] ?? valor),
          item.acessoriosOutros,
        ]
          .filter(Boolean)
          .join(', ') || '—',
      observacao: item.observacao?.trim() || '—',
    }));

    return {
      tipoFluxo: movimentacao.tipoFluxo,
      cidade: CIDADE_SEDE_RARO,
      data: new Date(),
      empresa,
      colaborador: {
        nome: colaborador.name,
        funcao: colaborador.positionName ?? '—',
        cpf: colaborador.cpf ?? '—',
        cidade: colaborador.addresses?.[0]?.city ?? CIDADE_SEDE_RARO,
      },
      dataDeclaracao,
      assinanteRaro: {
        nome: assinanteRaro?.name ?? user?.displayName ?? '',
        cpf: movimentacao.tipoFluxo === 'envio' ? (assinanteRaro?.cpf ?? undefined) : undefined,
      },
      equipamentos,
      gruposFotos: [
        ...itens.map((item) => ({
          legenda: legendaEquipamento(item),
          urls: item.fotos ?? [],
        })),
        // Legadas (de antes das fotos por equipamento) — ver Movimentacao.fotos.
        { legenda: 'Fotos anteriores', urls: movimentacao.fotos ?? [] },
      ].filter((grupo) => grupo.urls.length > 0),
    };
  };

  const nomeArquivoTermo = (dados: TermoPdfDados): string => {
    const patrimonios = itens.map((item) => item.patrimonio).filter(Boolean).join(', ');
    return sanitizarNomeArquivo(
      `${TITULO_ARQUIVO_POR_TIPO_FLUXO[movimentacao.tipoFluxo]} - ${dados.colaborador.nome} - ${patrimonios}.pdf`
    );
  };

  const handleVisualizar = async () => {
    if (!podeGerar) return;
    const dados = montarDadosTermo();
    if (!dados) return;
    setVisualizando(true);
    try {
      const blob = await termoPdfService.gerarTermoPdf(dados);
      // Não revoga a URL aqui de propósito — a aba recém-aberta ainda precisa dela; o
      // navegador libera a memória quando a aba fecha.
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (error: any) {
      toast.error(`Erro ao gerar a prévia: ${error?.message || 'erro desconhecido'}`);
    } finally {
      setVisualizando(false);
    }
  };

  const handleGerar = async () => {
    if (!podeGerar) return;
    const dados = montarDadosTermo();
    if (!dados) return;
    setGerando(true);
    try {
      const blob = await termoPdfService.gerarTermoPdf(dados);
      const nomeArquivo = nomeArquivoTermo(dados);

      // Baixa na hora (conveniência) e também guarda no Storage — sem isso, só existia o
      // download imediato, sem jeito de recuperar o mesmo PDF depois (ver termoUrl).
      const urlLocal = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlLocal;
      a.download = nomeArquivo;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(urlLocal);

      const arquivo = new File([blob], nomeArquivo, { type: 'application/pdf' });
      const termoUrl = await uploadFile(
        arquivo,
        `si_solicitacoes/${solicitacaoId}/movimentacoes/${movimentacao.id}/${nomeArquivo}`
      );

      // Gerar ≠ enviar: o termo é enviado pra assinatura por outra plataforma — o SI marca
      // "Termo Enviado" à mão quando enviar (automatizar só se o envio vier a ser automático).
      atualizarMovimentacao.mutate({ id: movimentacao.id, termoUrl });
      toast.success('Termo gerado e baixado. Depois de enviar pra assinatura, marque "Termo Enviado".');
    } catch (error: any) {
      toast.error(`Erro ao gerar o termo: ${error?.message || 'erro desconhecido'}`);
    } finally {
      setGerando(false);
    }
  };

  const handleClickGerar = () => {
    if (!podeGerar) return;
    if (faltaDataDeclaracao) {
      toast.error(avisoFaltaData);
      return;
    }
    // Cobrado só aqui (e na pendência), sem contador na tela — ver FotosEquipamentoSection.
    // "Visualizar" continua livre, pra conferir o termo antes das fotos.
    if (calcularEtapaFotos(movimentacao) !== 'concluido') {
      // Ex.: "Monitor (1234) tem 1 de 2 e Chip (5678) tem 0 de 1" — o mínimo é por item
      // (cartão de acesso e chip pedem 1), então cada um diz o seu, sem regra genérica.
      const semFotosSuficientes = itens
        .filter((item) => (item.fotos ?? []).length < fotosMinimasDoItem(item.tipoEquipamento))
        .map((item) => {
          const nome =
            item.tipoEquipamento && item.tipoEquipamento !== 'outros'
              ? TIPO_EQUIPAMENTO_LABELS[item.tipoEquipamento]
              : 'Equipamento';
          const contagem = `tem ${(item.fotos ?? []).length} de ${fotosMinimasDoItem(item.tipoEquipamento)}`;
          return item.patrimonio ? `${nome} (${item.patrimonio}) ${contagem}` : `${nome} ${contagem}`;
        });
      const lista =
        semFotosSuficientes.length > 1
          ? `${semFotosSuficientes.slice(0, -1).join(', ')} e ${semFotosSuficientes[semFotosSuficientes.length - 1]}`
          : semFotosSuficientes[0];
      toast.error(
        `Faltam fotos para gerar o termo: ${lista}.`
      );
      return;
    }
    // Já existe um termo gerado antes — pergunta antes de regerar à toa (o SI raramente
    // precisa gerar mais de uma vez, só quando algum dado muda depois da 1ª geração).
    if (movimentacao.termoUrl) {
      setMostrarEscolha(true);
      return;
    }
    handleGerar();
  };

  const handleBaixarExistente = () => {
    if (movimentacao.termoUrl) {
      window.open(movimentacao.termoUrl, '_blank');
    }
    setMostrarEscolha(false);
  };

  const handleConfirmarGerarNovamente = () => {
    setMostrarEscolha(false);
    handleGerar();
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        className="btn-secondary text-sm"
        onClick={handleVisualizar}
        disabled={!podeGerar || !colaborador || visualizando || gerando}
        title={!podeGerar ? 'Preencha patrimônio, modelo e condição de todos os equipamentos primeiro' : undefined}
      >
        {visualizando ? 'Abrindo...' : 'Visualizar'}
      </button>
      <button
        type="button"
        className="btn-secondary text-sm"
        onClick={handleClickGerar}
        disabled={!podeGerar || faltaDataDeclaracao || !colaborador || gerando || visualizando}
        title={
          !podeGerar
            ? 'Preencha patrimônio, modelo e condição de todos os equipamentos primeiro'
            : faltaDataDeclaracao
              ? avisoFaltaData
              : undefined
        }
      >
        {gerando ? 'Gerando...' : 'Gerar Termo'}
      </button>
      {podeGerar && faltaDataDeclaracao && <p className="w-full text-xs text-amber-700">{avisoFaltaData}</p>}

      <BaseModal
        isOpen={mostrarEscolha}
        onClose={() => setMostrarEscolha(false)}
        title="Termo já gerado"
        maxWidth="max-w-md"
        actions={
          <div className="flex justify-end gap-3">
            <button type="button" className="btn-secondary" onClick={handleBaixarExistente}>
              Baixar o já gerado
            </button>
            <button type="button" className="btn-primary" onClick={handleConfirmarGerarNovamente}>
              Gerar novamente
            </button>
          </div>
        }
      >
        <p className="py-2 text-sm text-gray-700">
          Já existe um termo gerado pra esta Movimentação. Quer baixar o que já foi gerado, ou gerar de novo com os
          dados atuais (use isso se algo mudou desde a última geração)?
        </p>
      </BaseModal>
    </div>
  );
}
